import { Copy, ExternalLink } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { MetaLine } from "@/components/ui/meta-line";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useCopyCitation } from "../hooks/useCopyCitation";
import { courtLabel } from "../sources";
import type { LibraryHit } from "../types";
import { Highlighted } from "./Highlighted";
import { WeightBadge } from "./WeightBadge";

type Props = {
  hit: LibraryHit;
  terms: string[];
  active?: boolean;
  onOpen: (hit: LibraryHit) => void;
};

/** One authority: what it is, what it is worth, and the passage that matched. */
export const ResultRow: FC<Props> = ({ hit, terms, active, onOpen }) => {
  const copy = useCopyCitation();
  return (
    <li
      className={cn(
        "group relative flex flex-col gap-1 px-4 py-3 transition-colors duration-100 hover:bg-fill-1",
        active && "bg-fill-3 hover:bg-fill-3"
      )}
      data-testid="research-result"
      data-active={active || undefined}
    >
      <button
        type="button"
        disabled={!hit.id}
        onClick={() => onOpen(hit)}
        className="absolute inset-0 rounded-none outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-label={`Open ${hit.title}`}
      />
      <div className="flex min-w-0 items-start gap-2">
        <span className="line-clamp-2 min-w-0 flex-1 text-base font-medium text-fg">
          {hit.title}
        </span>
        <div className="relative flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-100 group-focus-within:opacity-100 group-hover:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label="Copy Citation"
                data-testid="research-copy"
                onClick={() => copy(hit)}
              >
                <Copy />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Copy Citation</TooltipContent>
          </Tooltip>
          {hit.url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="icon-xs" variant="ghost" asChild>
                  <a
                    href={hit.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open Source"
                  >
                    <ExternalLink />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Open Source</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className="relative flex min-w-0 flex-wrap items-center gap-1.5">
        <WeightBadge weight={hit.weight} />
        {hit.citation && !hit.title.includes(hit.citation) ? (
          <Tag mono>{hit.citation}</Tag>
        ) : null}
        <MetaLine parts={[courtLabel(hit), hit.date]} />
      </div>
      {hit.snippet ? (
        <p className="line-clamp-3 text-sm text-fg-3">
          <Highlighted text={hit.snippet} terms={terms} />
        </p>
      ) : null}
    </li>
  );
};
